<section class="bg-star-gray-600 pt-[40px] pb-[56px] lg:pt-[64px] lg:pb-[72px]">
  <div
    class="mx-auto flex max-w-[1000px] flex-col gap-[36px] px-[20px] lg:flex-row lg:items-center lg:gap-[56px]"
  >
    <div class="lg:flex-[1.5]">
      <p class="text-[13px] uppercase tracking-[1.6px] text-star-blue-500">
        Solutions for payment providers
      </p>
      <h1
        class="mt-[14px] text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[42px]"
      >
        AML Screening for Crypto Payment Providers
      </h1>
      <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
        A payment provider sits between a payer it has never met and a merchant whose licence
        depends on what it accepts. AMLOfficer gives that position a screening layer: incoming
        addresses checked through four AML data providers, merchant wallets kept under monitoring,
        and the whole portfolio visible to one compliance team instead of scattered across the
        services it runs.
      </p>
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow mt-[28px] min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
      <p class="mt-[16px] text-[14px] leading-[1.6] text-white/50">
        Business terms depend on your volumes and are shown in your account after registration.
      </p>
    </div>
    <div class="flex justify-center lg:flex-1">
      <img
        src="./images/aml-api-desktop.png"
        alt="AML screening results delivered through the AMLOfficer API"
        width="360"
        height="240"
        class="w-full max-w-[360px] object-contain"
        loading="lazy"
      />
    </div>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      You carry risk that was created somewhere else
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      An exchange screens its own users. A payment provider screens strangers on behalf of someone
      else's business. The payer is a customer of your merchant, chosen by your merchant, paying
      into a flow you built — and if those funds carry exposure, it lands on your side of the
      settlement first. That is the structural difference, and it changes what compliance tooling
      has to do.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      It also multiplies. One merchant is a book of addresses; fifty merchants are fifty books, each
      with its own risk profile and its own settlement schedule. The hardest part of the job is not
      assessing one address well — it is keeping oversight across every merchant at once, which is
      exactly where scattered data across clients and services turns into missed anomalies.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      The compliance work a provider actually does
    </h2>
    <div class="mt-[32px] flex flex-col divide-y divide-white/10">
      <div class="flex flex-col gap-[8px] pb-[24px] md:flex-row md:gap-[32px]">
        <h3 class="text-[18px] font-medium leading-[1.4] text-white md:w-[260px] md:shrink-0">
          Screening an incoming payment
        </h3>
        <p class="text-[15px] leading-[1.75] text-white/70">
          The address a payment arrives from is read before the payment is treated as good funds.
          What comes back is a picture of what that money has been close to — enough to credit it,
          hold it, or put it in front of a person.
        </p>
      </div>
      <div class="flex flex-col gap-[8px] py-[24px] md:flex-row md:gap-[32px]">
        <h3 class="text-[18px] font-medium leading-[1.4] text-white md:w-[260px] md:shrink-0">
          Releasing a settlement
        </h3>
        <p class="text-[15px] leading-[1.75] text-white/70">
          Paying a merchant out is a decision about a destination address, and it is the moment your
          own exposure is fixed. Screening the payout address before it is signed keeps that
          decision documented rather than assumed.
        </p>
      </div>
      <div class="flex flex-col gap-[8px] py-[24px] md:flex-row md:gap-[32px]">
        <h3 class="text-[18px] font-medium leading-[1.4] text-white md:w-[260px] md:shrink-0">
          Onboarding and reviewing a merchant
        </h3>
        <p class="text-[15px] leading-[1.75] text-white/70">
          A merchant's own wallets say more about the business than its application form does.
          Screening them at onboarding, and again when the relationship changes, turns periodic
          review into something with evidence behind it.
        </p>
      </div>
      <div class="flex flex-col gap-[8px] pt-[24px] md:flex-row md:gap-[32px]">
        <h3 class="text-[18px] font-medium leading-[1.4] text-white md:w-[260px] md:shrink-0">
          Watching the portfolio over time
        </h3>
        <p class="text-[15px] leading-[1.75] text-white/70">
          Merchant wallets can be added manually or put under automated real-time monitoring, with
          the depth you choose: deposits only, withdrawals only, or full history. A merchant whose
          exposure changes after onboarding is the case this is built to catch.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Checks that live inside your own product
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      For a provider, a compliance tool that requires a separate login is a tool that will be used
      selectively. AMLOfficer is designed to be embedded instead: Fast API puts reports into a
      dashboard your team works in together, and Pro API feeds AML data straight into your own
      systems in real time, so the check runs where the payment is processed.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Commercial terms follow the same logic: they are agreed with the team around the integration
      you actually run, rather than picked from a published tier.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Four providers, because one dataset is not the whole picture
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer aggregates four AML providers, including BitOK, Global Ledger and Beosin. The point
      of aggregation is disagreement: providers draw on different datasets, and an address one of
      them calls clean is regularly flagged by another.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      A provider feels that gap sooner than most, because the same address can pass through several
      merchants in a week. Reading it through several sources at once means a merchant is not
      declined on one vendor's opinion, and not accepted on one vendor's silence.
    </p>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      One team, many merchants
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Oversight across a portfolio is a people problem before it is a data problem. Business
      accounts support role-based access and shared investigations, so account managers, the
      compliance officer and whoever is on duty can work on the same merchant case with different
      permissions and a shared history.
    </p>
    <ul class="mt-[24px] grid grid-cols-1 gap-[16px] md:grid-cols-3">
      <li class="rounded-[14px] bg-star-gray-600 p-[20px] shadow-inner-white">
        <span class="text-[15px] font-medium text-white">Roles per person</span>
        <p class="mt-[8px] text-[14px] leading-[1.7] text-white/70">
          Invite the people who need access and give each of them the level of access the job
          requires.
        </p>
      </li>
      <li class="rounded-[14px] bg-star-gray-600 p-[20px] shadow-inner-white">
        <span class="text-[15px] font-medium text-white">Alerts in Telegram</span>
        <p class="mt-[8px] text-[14px] leading-[1.7] text-white/70">
          Wallet reports and real-time alerts reach a Telegram group, which for most providers is
          where the duty channel already lives.
        </p>
      </li>
      <li class="rounded-[14px] bg-star-gray-600 p-[20px] shadow-inner-white">
        <span class="text-[15px] font-medium text-white">Records that hold up</span>
        <p class="mt-[8px] text-[14px] leading-[1.7] text-white/70">
          Reports stay with the address, so a question from a partner bank or a merchant months
          later has an answer.
        </p>
      </li>
    </ul>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      AML for payment providers: common questions
    </h2>
    <div class="mt-[28px] flex flex-col gap-[24px]">
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Can the check run inside our own checkout?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Yes — that is what Pro API is for. AML data is returned to your systems in real time, so
          the screening step is part of your payment flow rather than a separate task somebody
          remembers to do.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Can we separate what each merchant looks like?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Wallets are added individually and monitored individually, and the team works on them
          through shared investigations with role-based access — so the picture per merchant stays
          distinct while the oversight stays in one place.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Does this cover identity verification of our merchants?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          No. AMLOfficer works on blockchain addresses and the flows around them. Verifying who a
          merchant is remains a separate process; what this adds is what that merchant's money has
          been near.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          What does it cost to run at our volume?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Business pricing depends on volume and is shown in your account after registration rather
          than published as a tariff table.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-650 pt-[56px] pb-[72px] lg:pt-[72px] lg:pb-[96px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <div
      class="rounded-[20px] bg-star-gray-600 px-[24px] py-[36px] shadow-inner-white lg:px-[48px] lg:py-[48px]"
    >
      <h2 class="max-w-[620px] text-[24px] font-medium leading-[1.3] text-white lg:text-[30px]">
        Bring your merchant portfolio under one screening layer
      </h2>
      <p class="mt-[16px] max-w-[620px] text-[16px] leading-[1.75] text-white/70">
        Open a business account to add merchant wallets, invite your compliance team and see how the
        checks fit into the payment flow you already run.
      </p>
      <div class="mt-[28px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <a
          [routerLink]="'/contact-us'"
          class="text-[16px] leading-[1.6] text-white/70 underline underline-offset-4 hover:text-white"
        >
          Or ask about integrating the checks
        </a>
      </div>
    </div>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
